'use client';
import {
  Conditional,
  RequestServiceForm,
  RequestServiceModal,
  useBreakpoint
} from 'components/pcc-components';
import { useForm } from 'react-hook-form';
import {
  useCallback,
  useState,
  useEffect,
  useTransition,
  useMemo
} from 'react';
import { PAGE_BREAKPOINTS } from 'constants/common';
import {
  NO_PREFERENCE,
  REFERENCE_TYPE,
  REQUEST_SERVICE_FORM_ATTRIBUTES,
  REQUIST_SERVICE_CUSTOM_WIDTH
} from './constant';
import { useEsiteCtx } from 'services/esite/context';
import styles from './request-equipment-service.module.scss';
import { getRequestServicesAction } from 'services/request-service/action';
import { getFamilyName } from 'services/equipment/utils';
import { getSearchPayload } from './utils';
import RequestServiceNoEquipment from './no-equipment-modal';
import { RequestEquipmentServiceClientProps } from './types';
import { useRequestEquipmentService } from './request-equipment-service-context';
import RequestEquipmentServiceToast from './request-equipment-service-toast';
import RequestEquipmentServiceHeader from './request-equipment-service-header';
import RequestEquipmentServiceLeavePage from './request-equipment-service-leave-page';
import RequestEquipmentServiceOverviewModal from './request-equipment-service-overview-modal';
import RequestEquipmentServiceFooter from './request-equipment-service-footer';
import RequestEquipmentServiceBody from './request-equipment-service-body';
import RequestEquipmentServiceSearchLoader from './request-equipment-service-search-loader';

export const RequestEquipmentServiceClient = ({
  userDetails,
  dealerName,
  dealerCode,
  equipmentList,
  selectedEquipmentIndex,
  type,
  initialValue,
  partDetails,
  formLocation = ''
}: Readonly<RequestEquipmentServiceClientProps>) => {
  const {
    isRSModalOpen,
    setIsRSModalOpen,
    equipmentInfo,
    setEquipmentInfo,
    dealerAssociation
  } = useRequestEquipmentService();
  const isMd = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const [showToast, setShowToast] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [dismissNotification, setDismissNotification] = useState(false);
  const [isLeavePageModelActive, setIsLeavePageModelActive] = useState(false);
  const [existingServices, setExistingServices] = useState(null);
  const fullWidth = isMd === false;
  const { storeId } = useEsiteCtx();
  const {
    firstName = '',
    lastName = '',
    companyName = '',
    email = '',
    phone = '',
    ccidProd = ''
  } = userDetails;
  const fullName = `${firstName} ${lastName}`.trim();
  const [isOverviewModalOpen, setIsOverviewModalOpen] = useState(false);
  const [isLoading, startTransition] = useTransition();
  const isEmurge = type === REFERENCE_TYPE.TECH_UPGRADE;
  const selectedEquipment = useMemo(() => {
    return equipmentList?.length && selectedEquipmentIndex > -1
      ? equipmentList[selectedEquipmentIndex]
      : {};
  }, [equipmentList, selectedEquipmentIndex]);
  useEffect(() => {
    const getExistingServices = async () => {
      if (equipmentInfo.serialNumber || equipmentInfo.name) {
        const searchpayload = getSearchPayload(equipmentInfo, type);
        const { response } = await getRequestServicesAction(searchpayload);
        setExistingServices(response);
      }
    };
    startTransition(() => {
      getExistingServices();
    });
  }, [equipmentInfo, isRSModalOpen, type]);

  useEffect(() => {
    if (isEmurge) {
      setEquipmentInfo(selectedEquipment);
    }
  }, [isEmurge, selectedEquipment, setEquipmentInfo]);

  const formMethods = useForm({
    mode: 'all',
    defaultValues: {
      equipment: equipmentInfo,
      requestDescription: '',
      requestDealerName: dealerName ?? '',
      requestDealerCode: dealerCode ?? '',
      requestServiceStoreId: storeId ?? '',
      ...initialValue,
      preferredDateTime: {
        time: NO_PREFERENCE.value,
        dates: [],
        type: REQUEST_SERVICE_FORM_ATTRIBUTES.prefDateType.asap,
        month: new Date()
      },
      customer: {
        companyName: companyName,
        contactName: fullName ?? '',
        contactMethod: REQUEST_SERVICE_FORM_ATTRIBUTES.contactType.email,
        email: email ?? '',
        phone: phone ?? '',
        customerName: fullName,
        ccidProd: ccidProd
      }
    }
  });

  const requestDealerName = formMethods.watch('requestDealerName');

  const scrollToError = () => {
    const logoContainer = document.getElementsByClassName(
      'request-service-error'
    );
    if (logoContainer.length > 0) {
      logoContainer[0].scrollIntoView(false);
    }
  };

  const onError = () => {
    scrollToError();
  };

  const onSubmit = () => {
    const currentIndex = activeIndex + 1;
    if (currentIndex < 3) {
      setActiveIndex((prev) => prev + 1);
    }
    if (currentIndex > 2 || isMd) {
      setIsOverviewModalOpen(true);
    }
  };

  const modalClose = useCallback(() => {
    setIsRSModalOpen(false);
    setActiveIndex(0);
    setDismissNotification(false);
  }, [setIsRSModalOpen, setActiveIndex]);

  const resetFormOnModelClose = useCallback(() => {
    formMethods.reset();
    modalClose();
    if (isEmurge) {
      setEquipmentInfo(selectedEquipment);
    }
  }, [formMethods, modalClose, selectedEquipment, setEquipmentInfo, isEmurge]);

  const onRequestModalClose = useCallback(
    (e: Event) => {
      e.preventDefault();
      if (formMethods.formState.isDirty) {
        return setIsLeavePageModelActive(true);
      }
      return resetFormOnModelClose();
    },
    [formMethods.formState.isDirty, resetFormOnModelClose]
  );

  const onBackCancel = useCallback(
    (e: Event) => {
      if (isMd) {
        onRequestModalClose(e);
      } else {
        setActiveIndex((prev) => prev - 1);
      }
    },
    [isMd, onRequestModalClose]
  );

  const onDealerChange = (item) => {
    setShowToast(false);
    if (requestDealerName !== item.label) {
      formMethods.setValue('requestDealerName', item.label);
      formMethods.setValue('requestServiceStoreId', item.value);
      setShowToast(true);
    }
  };

  const handleClick = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.stopPropagation();
  };

  const hasEquipment = equipmentList?.length > 0;
  const { model, productFamily, name } = equipmentInfo;
  const equipmentDetails = {
    ...equipmentInfo,
    equipmentFamily: getFamilyName(model, name ?? productFamily)
  };

  useEffect(() => {
    if (formMethods.formState.errors) {
      scrollToError();
    }
  }, [formMethods.formState.errors]);

  return (
    <Conditional
      test={hasEquipment}
      fallback={
        <RequestServiceNoEquipment
          modalClose={modalClose}
          isRSModalOpen={isRSModalOpen}
        />
      }
    >
      <RequestServiceForm
        formMethods={formMethods}
        handleSubmit={formMethods.handleSubmit(onSubmit, onError)}
      >
        <RequestServiceModal
          onBlModalCancel={onRequestModalClose}
          isActive={isRSModalOpen}
          disableBackdropClick={true}
          disableEscClick={true}
          fullWidth={fullWidth}
          width={REQUIST_SERVICE_CUSTOM_WIDTH}
          className={styles['request-service-modal']}
          onClick={handleClick}
          returnFocusOnDeactivate={false}
        >
          <Conditional test={showToast}>
            <RequestEquipmentServiceToast setShowToast={setShowToast} />
          </Conditional>
          <RequestEquipmentServiceHeader />
          <Conditional
            test={!isLoading}
            fallback={<RequestEquipmentServiceSearchLoader />}
          >
            <RequestEquipmentServiceBody
              equipmentDetail={equipmentDetails}
              dealerName={requestDealerName}
              onDealerChange={onDealerChange}
              dealerList={dealerAssociation}
              activeIndex={activeIndex}
              fullWidth={fullWidth}
              type={type}
              existingServices={existingServices}
              equipmentList={equipmentList}
              setDismissNotification={setDismissNotification}
              dismissNotification={dismissNotification}
            />
          </Conditional>
          <RequestEquipmentServiceFooter
            activeIndex={activeIndex}
            isMd={isMd}
            onBackCancel={onBackCancel}
            disableCTA={!hasEquipment}
            isLoading={isLoading}
          />
        </RequestServiceModal>
        <RequestEquipmentServiceOverviewModal
          isModalActive={isOverviewModalOpen}
          setIsModalActive={setIsOverviewModalOpen}
          requestServiceInfo={formMethods.getValues()}
          equipmentDetails={equipmentDetails}
          modalClose={modalClose}
          resetFormOnModelClose={resetFormOnModelClose}
          submitterEmail={email}
          partDetails={partDetails}
          type={type}
          formLocation={formLocation}
        />
        <RequestEquipmentServiceLeavePage
          isLeavePageModelActive={isLeavePageModelActive}
          setIsLeavePageModelActive={setIsLeavePageModelActive}
          modalClose={resetFormOnModelClose}
        />
      </RequestServiceForm>
    </Conditional>
  );
};
